import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ROUTES } from "@/lib/routes";
import { authClient } from "@/lib/auth-client";

export default function VerifyMagicLink() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState("Verifying your magic link...");

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get("token");
      
      if (!token) {
        setStatus('error');
        setMessage("Invalid verification link. Please request a new one.");
        return;
      }

      try {
        // Use Better Auth's magicLink signIn method with the token from query params
        const { data, error } = await authClient.signIn.magicLink({
          query: { token }
        });

        if (error) {
          throw error;
        }

        setStatus('success');
        setMessage("Successfully verified! Redirecting...");
        
        // Redirect to dashboard
        setTimeout(() => {
          navigate(ROUTES.DASHBOARD);
        }, 1500);

      } catch (error: any) {
        console.error("Verification error:", error);
        setStatus('error');
        setMessage(error.message || "Failed to verify magic link. It may have expired.");
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            {status === 'verifying' && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
            {status === 'success' && <CheckCircle2 className="h-6 w-6 text-green-500" />}
            {status === 'error' && <XCircle className="h-6 w-6 text-destructive" />}
          </div>
          <CardTitle>
            {status === 'verifying' && "Verifying..."}
            {status === 'success' && "Welcome Back!"}
            {status === 'error' && "Verification Failed"}
          </CardTitle>
          <CardDescription>
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'error' && (
            <Button onClick={() => navigate(ROUTES.AUTH)} className="w-full">
              Back to Sign In
            </Button>
          )}
          {status === 'success' && (
            <Button onClick={() => navigate(ROUTES.DASHBOARD)} className="w-full" variant="outline">
              Go to Dashboard Now
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
